import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toggle", "lightBtn", "darkBtn", "systemBtn", "sunIcon", "moonIcon"]

  connect() {
    this.updateToggle()
    this.updateTheme()
    this.updateButtons()
    this.updateIcons()

    // Watch for OS theme preference changes
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      // Only update theme based on OS preference if user hasn't set a preference
      if (!('theme' in localStorage)) {
        this.updateTheme()
        this.updateButtons()
        this.updateIcons()
      }
    })
  }

  updateToggle() {
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = this.isDarkMode()
    }
  }

  toggle() {
    if (this.isDarkMode()) {
      localStorage.theme = 'light'
    } else {
      localStorage.theme = 'dark'
    }
    this.updateTheme()
    this.updateIcons()
  }

  setLight() {
    localStorage.theme = 'light'
    this.updateTheme()
    this.updateButtons()
  }

  setDark() {
    localStorage.theme = 'dark'
    this.updateTheme()
    this.updateButtons()
  }

  setSystem() {
    localStorage.removeItem('theme')
    this.updateTheme()
    this.updateButtons()
  }

  updateButtons() {
    const activeClasses = ['bg-blue-600', 'text-white', 'ring-2', 'ring-blue-300']
    const inactiveClasses = ['bg-gray-100', 'dark:bg-gray-600', 'text-gray-700', 'dark:text-gray-200']

    // Reset all buttons to inactive state
    const allButtons = [
      this.hasLightBtnTarget && this.lightBtnTarget,
      this.hasDarkBtnTarget && this.darkBtnTarget,
      this.hasSystemBtnTarget && this.systemBtnTarget
    ].filter(Boolean)

    allButtons.forEach(btn => {
      activeClasses.forEach(cls => btn.classList.remove(cls))
      inactiveClasses.forEach(cls => {
        const classes = cls.split(':')
        if (classes.length > 1) {
          // Handle dark: prefixed classes
          if (!btn.classList.contains(cls)) btn.classList.add(cls)
        } else {
          if (!btn.classList.contains(cls)) btn.classList.add(cls)
        }
      })
    })

    // Highlight the active button
    let activeButton
    if (localStorage.theme === 'light') {
      activeButton = this.hasLightBtnTarget && this.lightBtnTarget
    } else if (localStorage.theme === 'dark') {
      activeButton = this.hasDarkBtnTarget && this.darkBtnTarget
    } else {
      activeButton = this.hasSystemBtnTarget && this.systemBtnTarget
    }

    if (activeButton) {
      inactiveClasses.forEach(cls => activeButton.classList.remove(cls))
      activeClasses.forEach(cls => activeButton.classList.add(cls))
    }
  }

  isDarkMode() {
    return localStorage.theme === 'dark' ||
      (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)
  }

  updateTheme() {
    const darkModeEnabled = this.isDarkMode()
    console.info('isDarkMode', darkModeEnabled)

    if (darkModeEnabled) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    // Update the toggle after theme changes, but not inside isDarkMode check
    // to avoid potential circular logic
    this.updateToggle()
  }

  updateIcons() {
    const isDark = this.isDarkMode()
    if (this.hasSunIconTarget && this.hasMoonIconTarget) {
      if (isDark) {
        this.sunIconTarget.classList.remove('hidden')
        this.moonIconTarget.classList.add('hidden')
      } else {
        this.sunIconTarget.classList.add('hidden')
        this.moonIconTarget.classList.remove('hidden')
      }
    }
  }
}
